(() => {
  'use strict';

  const mounted = new WeakMap();
  const selector = '[data-toilet-pdp]';

  const initialize = (root) => {
    if (mounted.has(root)) return;

    const controller = new AbortController();
    const listen = (node, type, handler, options = {}) => node?.addEventListener(type, handler, {...options, signal: controller.signal});
    const one = (query) => root.querySelector(query);
    const all = (query) => Array.from(root.querySelectorAll(query));
    const variantInput = one('[data-jj-variant-select]');
    const quantityInput = one('.jj-product-form [name="quantity"]') || one('input[data-jj-quantity]');
    const optionButtons = all('[data-tp-option-index][data-tp-option-value]');
    const quantityButtons = all('[data-tp-quantity-step]');
    const stage = one('[data-jj-gallery-current]');
    const dialog = one('[data-tp-dialog]');
    const zoomContent = dialog?.querySelector('[data-tp-zoom-content]');
    let data = {};
    try { data = JSON.parse(one('[data-jj-product-data]')?.textContent || '{}'); } catch (_) {}
    const sourceVariants = Array.isArray(data?.variants) ? data.variants : [];
    const variants = sourceVariants.filter((variant) => variant && variant.id != null && Array.isArray(variant.options));
    const selected = () => variants.find((variant) => String(variant.id) === String(variantInput?.value));
    const optionIndex = (button) => {
      const value = Number(button.dataset.tpOptionIndex);
      return Number.isSafeInteger(value) && value >= 0 ? value : -1;
    };
    const candidateFor = (button, current = selected()) => {
      const index = optionIndex(button);
      if (!current || index < 0 || index >= current.options.length) return null;
      const tuple = current.options.map(String);
      tuple[index] = button.dataset.tpOptionValue;
      return variants.find((variant) => variant.options.length === tuple.length && variant.options.every((value, position) => String(value) === tuple[position])) || null;
    };
    let disposed = false;
    let zoomOpener = null;
    let previousOverflow = null;

    const positiveInteger = (value, fallback) => {
      const parsed = Number(value);
      return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
    };
    const limits = () => {
      const variant = selected();
      const min = positiveInteger(variant?.quantityMin, 1);
      const step = positiveInteger(variant?.quantityIncrement, 1);
      const rawMax = positiveInteger(variant?.quantityMax, Infinity);
      const max = Number.isFinite(rawMax) ? min + Math.floor((rawMax - min) / step) * step : Infinity;
      return {min, step, max};
    };
    const updateQuantityButtons = () => {
      if (!quantityInput) return;
      const {min, max} = limits();
      const quantity = Number(quantityInput.value);
      const unavailable = selected()?.available === false || max < min;
      quantityButtons.forEach((button) => {
        const direction = Number(button.dataset.tpQuantityStep);
        const disabled = unavailable || (direction < 0 ? quantity <= min : quantity >= max);
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
      });
    };
    const syncQuantity = (value = quantityInput?.value) => {
      if (!quantityInput) return;
      const {min, step, max} = limits();
      quantityInput.min = String(min);
      quantityInput.step = String(step);
      if (Number.isFinite(max)) quantityInput.max = String(max);
      else quantityInput.removeAttribute('max');
      let quantity = Number(value);
      if (!Number.isSafeInteger(quantity)) quantity = min;
      quantity = Math.max(min, Math.min(max, quantity));
      quantity = min + Math.floor((quantity - min) / step) * step;
      quantityInput.value = String(quantity);
      updateQuantityButtons();
      return quantity;
    };
    const syncOptions = () => {
      const current = selected();
      if (!current) return;
      optionButtons.forEach((button) => {
        const index = optionIndex(button);
        const pressed = index >= 0 && String(current.options[index]) === button.dataset.tpOptionValue;
        const exists = Boolean(candidateFor(button, current));
        button.setAttribute('aria-pressed', String(pressed));
        button.setAttribute('aria-disabled', String(!exists));
        button.disabled = !exists;
      });
      all('[data-tp-selected-option]').forEach((label) => {
        const index = Number(label.dataset.tpSelectedOption);
        if (Number.isSafeInteger(index) && current.options[index] != null) label.textContent = String(current.options[index]);
      });
    };
    const copyZoomMedia = () => {
      if (!stage || !zoomContent) return;
      zoomContent.replaceChildren(...Array.from(stage.childNodes, (node) => node.cloneNode(true)));
      zoomContent.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; image.sizes = image.hasAttribute('data-jj-cropped') ? '(max-width: 900px) 213.333vw, 2347px' : '(max-width: 900px) 100vw, 1100px'; });
    };
    const restoreZoom = () => {
      if (previousOverflow !== null) {
        document.body.style.overflow = previousOverflow;
        previousOverflow = null;
      }
      if (!disposed && zoomOpener?.isConnected) zoomOpener.focus({preventScroll: true});
      zoomOpener = null;
    };
    const closeZoom = () => {
      if (dialog?.open) dialog.close();
      restoreZoom();
    };
    const openZoom = (button) => {
      if (!dialog || !zoomContent || !stage?.childNodes.length || typeof dialog.showModal !== 'function') return;
      copyZoomMedia();
      if (dialog.open) return;
      zoomOpener = button;
      try { dialog.showModal(); } catch (_) { zoomOpener = null; return; }
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      dialog.querySelector('[data-tp-close]')?.focus({preventScroll: true});
    };
    const synchronize = () => {
      syncOptions();
      syncQuantity();
      if (dialog?.open) copyZoomMedia();
    };

    listen(root, 'click', (event) => {
      const target = event.target?.closest?.('[data-tp-option-index][data-tp-option-value], [data-tp-quantity-step], [data-tp-zoom], [data-tp-close]');
      if (!target || target.closest(selector) !== root) return;
      if (target.hasAttribute('data-tp-option-index')) {
        event.preventDefault();
        if (!variantInput || target.disabled) return;
        const variant = candidateFor(target);
        if (!variant) return;
        if (String(variant.id) !== String(variantInput.value)) {
          variantInput.value = String(variant.id);
          variantInput.dispatchEvent(new Event('change', {bubbles: true}));
        }
        synchronize();
      } else if (target.hasAttribute('data-tp-quantity-step')) {
        event.preventDefault();
        event.stopPropagation();
        if (!quantityInput || target.disabled) return;
        const direction = Number(target.dataset.tpQuantityStep);
        if (direction !== -1 && direction !== 1) return;
        const quantity = syncQuantity();
        syncQuantity(quantity + direction * limits().step);
        quantityInput.dispatchEvent(new Event('change', {bubbles: true}));
      } else if (target.hasAttribute('data-tp-close')) {
        event.preventDefault();
        closeZoom();
      } else {
        event.preventDefault();
        openZoom(target);
      }
    }, {capture: true});

    listen(variantInput, 'change', synchronize);
    listen(quantityInput, 'change', () => syncQuantity());
    listen(root, 'sopami:variantchange', synchronize);
    listen(dialog, 'cancel', (event) => { event.preventDefault(); closeZoom(); });
    listen(dialog, 'close', restoreZoom);
    listen(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeZoom();
    });

    const dispose = () => {
      disposed = true;
      closeZoom();
      controller.abort();
      root.classList.remove('tp-enhanced');
      mounted.delete(root);
    };
    mounted.set(root, dispose);
    synchronize();
    const coversAllOptions = variants.length > 0 && variants.length === sourceVariants.length && variants.every((variant) => variant.options.length > 0 && variant.options.every((value, index) => optionButtons.some((button) => optionIndex(button) === index && button.dataset.tpOptionValue === String(value))));
    if (variantInput && selected() && coversAllOptions) root.classList.add('tp-enhanced');
  };

  const scan = (scope = document) => {
    if (scope.matches?.(selector)) initialize(scope);
    scope.querySelectorAll?.(selector).forEach(initialize);
  };
  const unload = (scope) => {
    if (scope.matches?.(selector)) mounted.get(scope)?.();
    scope.querySelectorAll?.(selector).forEach((root) => mounted.get(root)?.());
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), {once: true});
  else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
